import { useEffect, useState, useCallback } from "react";
import {
  Loader2, Clapperboard, History, Lightbulb, TrendingUp, BarChart3, ClipboardList, CheckCircle2, ChevronRight,
} from "lucide-react";
import { api } from "../lib/api";
import { FilmRoom } from "../components/FilmRoom";
import { SportTabs } from "../components/SportTabs";
import { labelFor } from "../lib/sports";
import { useSport } from "../context/SportContext";
import { TeamLogo } from "../components/TeamLogo";

const CAT_ICON = { Market: TrendingUp, Stats: BarChart3, "Game Plan": ClipboardList };
const CAT_COLOR = { Market: "#A855F7", Stats: "#00E5FF", "Game Plan": "#F59E0B" };

const GameRow = ({ g, onOpen }) => {
  const aWin = Number(g.away.score) > Number(g.home.score);
  return (
    <button
      data-testid={`result-game-${g.matchup_id}`}
      onClick={() => onOpen(g.matchup_id, g.event_id)}
      className="w-full flex items-center justify-between gap-3 rounded-xl border border-[#233248] bg-[#141C2B] p-3 hover:border-[#00E5FF]/50 transition-colors text-left"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="font-data text-sm">
          <div className={`flex items-center gap-2 ${aWin ? "text-white font-bold" : "text-slate-400"}`}>
            <TeamLogo sport={g.sport} abbr={g.away.abbr} logo={g.away.logo} size="sm" />
            <span className="w-28 truncate">{g.away.name || g.away.abbr}</span>
            <span className="tabular-nums">{g.away.score}</span>
          </div>
          <div className={`flex items-center gap-2 ${!aWin ? "text-white font-bold" : "text-slate-400"}`}>
            <TeamLogo sport={g.sport} abbr={g.home.abbr} logo={g.home.logo} size="sm" />
            <span className="w-28 truncate">{g.home.name || g.home.abbr}</span>
            <span className="tabular-nums">{g.home.score}</span>
          </div>
        </div>
        <div className="min-w-0">
          <div className="font-data text-[11px] text-slate-500">{g.short_detail}</div>
          <div className="text-[11px] text-slate-400">
            {g.prop_count} props
            {g.analyzed && <span className="text-[#10B981] ml-1.5">· studied</span>}
          </div>
        </div>
      </div>
      <span className="shrink-0 inline-flex items-center gap-1 rounded-lg bg-[#00E5FF]/10 text-[#00E5FF] px-2.5 py-1.5 text-xs font-semibold">
        <Clapperboard className="h-3.5 w-3.5" /> Film Room <ChevronRight className="h-3.5 w-3.5" />
      </span>
    </button>
  );
};

export default function Results() {
  const { sport, setSport } = useSport();
  const [tab, setTab] = useState("games");
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [lessons, setLessons] = useState({ total: 0, by_category: {} });
  const [lessonsLoading, setLessonsLoading] = useState(false);

  const loadGames = useCallback(() => {
    setLoading(true);
    setSelected(null);
    setSelectedEvent(null);
    api.get(`/results/${sport}`).then((r) => setGames(r.data.games)).finally(() => setLoading(false));
  }, [sport]);

  const loadLessons = useCallback(() => {
    setLessonsLoading(true);
    api.get("/lessons", { params: { sport } }).then((r) => setLessons(r.data)).finally(() => setLessonsLoading(false));
  }, [sport]);

  useEffect(() => { loadGames(); }, [loadGames]);
  useEffect(() => { if (tab === "lessons") loadLessons(); }, [tab, loadLessons]);

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-6 py-6">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-white mb-1 flex items-center gap-2">
        <History className="h-6 w-6 text-[#00E5FF]" /> Results & Film Room
      </h1>
      <p className="text-slate-400 text-sm mb-5">
        Study finished games — grade every AI pick &amp; market lean against the final box score and see what to adjust next time.
      </p>

      {/* Sport tabs */}
      <div className="mb-4">
        <SportTabs value={sport} onChange={setSport} />
      </div>

      {/* View tabs */}
      <div className="flex gap-2 mb-5">
        <button
          data-testid="results-tab-games"
          onClick={() => { setTab("games"); setSelected(null); }}
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
            tab === "games" ? "bg-[#1B273E] text-white border border-[#00E5FF]/40" : "text-slate-400 border border-[#233248] hover:text-white"
          }`}
        >
          <Clapperboard className="h-4 w-4" /> Finished Games
        </button>
        <button
          data-testid="results-tab-lessons"
          onClick={() => setTab("lessons")}
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
            tab === "lessons" ? "bg-[#1B273E] text-white border border-[#A855F7]/40" : "text-slate-400 border border-[#233248] hover:text-white"
          }`}
        >
          <Lightbulb className="h-4 w-4" /> Lessons Learned
        </button>
      </div>

      {tab === "games" ? (
        selected ? (
          <FilmRoom matchupId={selected} eventId={selectedEvent} onBack={() => { setSelected(null); setSelectedEvent(null); loadGames(); }} />
        ) : loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#00E5FF]" /></div>
        ) : games.length === 0 ? (
          <div data-testid="results-empty" className="rounded-2xl border border-dashed border-[#233248] bg-[#141C2B]/50 p-10 text-center">
            <History className="h-8 w-8 text-slate-600 mx-auto mb-3" />
            <h3 className="font-heading text-white font-bold">No finished {labelFor(sport)} games yet</h3>
            <p className="text-sm text-slate-400 mt-1 max-w-md mx-auto">
              Completed games from today's slate show up here once they go final. Check back after games wrap up.
            </p>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="results-games-list">
            {games.map((g) => (
              <GameRow
                key={g.event_id || g.matchup_id}
                g={g}
                onOpen={(mid, eid) => { setSelected(mid); setSelectedEvent(eid); }}
              />
            ))}
          </div>
        )
      ) : lessonsLoading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#00E5FF]" /></div>
      ) : lessons.total === 0 ? (
        <div data-testid="lessons-empty" className="rounded-2xl border border-dashed border-[#233248] bg-[#141C2B]/50 p-10 text-center">
          <Lightbulb className="h-8 w-8 text-slate-600 mx-auto mb-3" />
          <h3 className="font-heading text-white font-bold">No lessons logged yet</h3>
          <p className="text-sm text-slate-400 mt-1 max-w-md mx-auto">
            Open the Film Room on a finished game and the AI's patterns to watch get saved here, building up over time.
          </p>
        </div>
      ) : (
        <div data-testid="lessons-list">
          <div className="flex items-center gap-2 mb-4 text-sm text-slate-400">
            <CheckCircle2 className="h-4 w-4 text-[#10B981]" /> {lessons.total} patterns logged from studied {labelFor(sport)} games
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {["Market", "Stats", "Game Plan"].map((cat) => {
              const items = lessons.by_category[cat] || [];
              const Icon = CAT_ICON[cat] || Lightbulb;
              const c = CAT_COLOR[cat];
              return (
                <div key={cat} className="rounded-2xl border border-[#233248] bg-[#141C2B] p-4" data-testid={`lessons-cat-${cat.replace(/\s/g, "").toLowerCase()}`}>
                  <h3 className="flex items-center gap-1.5 font-heading font-bold text-sm mb-3" style={{ color: c }}>
                    <Icon className="h-4 w-4" /> {cat}
                    <span className="font-data text-[11px] text-slate-500">{items.length}</span>
                  </h3>
                  {items.length === 0 ? (
                    <p className="text-xs text-slate-500">Nothing logged yet.</p>
                  ) : (
                    <ul className="space-y-2.5">
                      {items.map((l, i) => (
                        <li key={i} className="rounded-lg bg-[#0B0F17] p-2.5" style={{ boxShadow: `inset 3px 0 0 ${c}` }}>
                          <p className="text-sm text-slate-300 leading-snug">{l.insight}</p>
                          <div className="font-data text-[10px] text-slate-500 mt-1">{l.game}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
